import React, { useMemo, useState } from "react";
import { Formik, Form, Field, FormikProps } from "formik";
import cloneDeep from "lodash.clonedeep";
import isEmpty from "lodash/isEmpty";
import { useDispatch } from "react-redux";

import { FormikInputWithLabel } from "newui/components/ui/InputWithLabel/FormikInputWithLabel";
import { FormikRichTextEditor } from "newui/components/ui/RichTextEditor/FormikRichTextEditor";
import { FormikImagePreview } from "newui/components/ui/ImagePreview";
import { SectorSelect } from "newui/components/ui/CommonSelects/SectorSelect";
import { CompanySizeSelect } from "newui/components/ui/CommonSelects/CompanySizeSelect";
import { GlobalPresenceSelect } from "newui/components/ui/CommonSelects/GlobalPresenceSelect";
import { SpecialitySelect } from "newui/components/ui/CommonSelects/SpecialitySelect";
import { TradeSelect } from "newui/components/ui/CommonSelects/TradeSelect";
import { ExpertiseRepeater } from "components/FormInputs/ExpertiseRepeater";

import { FormikAutoSave } from "newui/components/ui/AutoSave";
import { updateCompanyInformation } from "../../company-api";
import { getValidationSchema } from "./CompanyInformationForm.validation";
import type { Company, CompanyInformationFormValues } from "../../types";
import { AccountTypeEnum } from "../../types/AccountType.type";
import { formatABN, showToast } from "utils/helpers";
import * as constants from "actions/actionTypes";
import type { SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import type { AppDispatch } from "newui/store";

/* -------------------------------------------------------------------------- */
/* Constants                                                                   */
/* -------------------------------------------------------------------------- */

const ACCOUNT_STATUS_OPTIONS: SelectOption[] = [
	{ label: "Hidden", value: "hidden" },
	{ label: "Visible", value: "published" },
];

const ERROR_MESSAGES: Record<string, { description: string; heading: string; }> = {
	required: {
		heading: "* Required Field",
		description: "There are incomplete required fields. Please complete them.",
	},
	connection: {
		heading: "Connection not established",
		description: "Unable to process your request",
	},
	maxCharacter: {
		heading: "Input has reached max character",
		description: "Input has reached max character",
	},
};

/* -------------------------------------------------------------------------- */
/* Props                                                                       */
/* -------------------------------------------------------------------------- */

interface CompanyInformationFormProps {
	company: Company;
	formikRef?: React.MutableRefObject<FormikProps<CompanyInformationFormValues> | null>;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

const CompanyInformationForm: React.FC<CompanyInformationFormProps> = ({
	company,
	formikRef,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const [errorStatus, setErrorStatus] = useState(false);
	const [showToastOnSave, setShowToastOnSave] = useState(false);
	const [businessNameError, setBusinessNameError] = useState<string | null>(null);

	const profileTypeId = company?.profile_type?.id ?? 1;
	const isClient = profileTypeId === 1;
	const isHC = profileTypeId === 2;
	const isSub = profileTypeId === 3;
	const isConsultant = profileTypeId === 4;

	const isSponsoredAccount =
		Number.parseInt(company.account_type_id) ===
		AccountTypeEnum.Sponsored_Bidder;

	const validationSchema = useMemo(
		() => getValidationSchema(profileTypeId),
		[profileTypeId]
	);

	const initialValues = useMemo<CompanyInformationFormValues>(
		() => ({
			logo: company?.logo_path || "",
			banner_image: company?.banner_path || "",
			name: company?.name || "",
			company_number: company?.company_number || "",
			sector_ids: Array.isArray(company?.sector)
				? company.sector.map((s) => s.id)
				: [],
			speciality_ids: Array.isArray(company?.speciality)
				? company.speciality.map((s) => s.id)
				: [],
			size_type_id: company?.size_type_id || "",
			description: company?.description?.replace(/<[^>]*>/g, "").trim() ? company.description : "",
			keywords: company?.keywords || [],
			services: company?.services || [],
			contact_name: company?.contact_name || "",
			phone: company?.phone || "",
			email: company?.email || "",
			website: company?.website || "",
			company_id: company?.id || "",
			status: company?.status === "draft" ? "published" : company?.status || "",
			disciplines:
				company?.disciplinesFormatted?.length
					? company.disciplinesFormatted
					: [{ expertise: "", disciplines: [] }],
			profile_type: profileTypeId,
			trade_ids: Array.isArray(company?.trade)
				? company.trade.map((t: any) => t.id)
				: [],
			years_trading: parseInt(company?.years_trading) || "",
			global_presence:
				company?.global_presence?.map((g) => ({
					value: g.id,
					label: g.name,
				})) ?? [],
			abn: company?.abn || "",
			indigenous_business: company?.indigenous_business ? "true" : "false",
		}),
		[company, profileTypeId]
	);

	const handleSubmit = async (
		values: CompanyInformationFormValues,
		{ setStatus }: { setStatus: (status: string) => void }
	) => {
		try {
			const submitValues = cloneDeep(values);

			// Convert indigenous_business string to boolean
			(submitValues as any).indigenous_business =
				values.indigenous_business === "true";

			// Map global_presence to IDs for the API
			if (submitValues.global_presence) {
				(submitValues as any).global_presence =
					submitValues.global_presence.map(
						(g) => (g as any).id || g.value
					);
			}

			const result = await updateCompanyInformation(
				submitValues.company_id as number,
				submitValues as any
			);

			const companyData = (result.data as any)?.data ?? result.data;

			// Sync Redux state
			dispatch({
				type: constants.SUBMIT_COMPANY_PROFILE_FORM,
				payload: companyData,
			});

			if (companyData?.logo_path) {
				dispatch({
					type: constants.UPDATE_COMPANY_LOGO,
					payload: companyData.logo_path,
				});
			}

			setBusinessNameError(null);

			if (showToastOnSave) {
				showToast("Data updated successfully.", "Success", true);
				setShowToastOnSave(false);
			}
		} catch (e: any) {
			const error = e.response?.data;
			setErrorStatus(true);
			setStatus(error?.message || "connection");

			if (error?.message === "The name has already been taken.") {
				setBusinessNameError(error.message);
			}

			const msg = error?.message ?? "Unexpected Error";
			if (showToastOnSave) {
				showToast(`Failed to save Company data - ${msg}`);
				setShowToastOnSave(false);
			}
		}
	};

	/* ---------------------------------------------------------------------- */
	/* Select change handlers                                                  */
	/* ---------------------------------------------------------------------- */

	const makeSelectHandler = (
		fieldName: string,
		mapValue: (selected: SelectOption | SelectOption[] | null) => any
	) => (
		setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
	) => (selected: SelectOption | SelectOption[] | null) => {
		setFieldValue(fieldName, mapValue(selected), true);
	};

	const handleSectorChange = makeSelectHandler("sector_ids", (s) =>
		Array.isArray(s) ? s.map((o) => o.value) : []
	);

	const handleSpecialityChange = makeSelectHandler("speciality_ids", (s) =>
		Array.isArray(s) ? s.map((o) => o.value) : []
	);

	const handleTradeChange = makeSelectHandler("trade_ids", (s) =>
		Array.isArray(s) ? s.map((o) => o.value) : []
	);

	const handleGlobalPresenceChange = makeSelectHandler("global_presence", (s) =>
		Array.isArray(s) ? s.map((o) => ({ value: o.value, label: o.label })) : []
	);

	const handleCompanySizeChange = makeSelectHandler("size_type_id", (s) =>
		s && !Array.isArray(s) ? s.value : ""
	);

	/* ---------------------------------------------------------------------- */
	/* Render                                                                  */
	/* ---------------------------------------------------------------------- */

	return (
		<Formik<CompanyInformationFormValues>
			innerRef={formikRef}
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{(formikProps) => {
				const {
					setFieldValue,
					isSubmitting,
					values,
					status,
					errors,
				} = formikProps;

				return (
					<Form onChange={() => setErrorStatus(false)}>
						<FormikAutoSave debounceMs={3000} hasError={errorStatus} />

						<div>
							{/* Profile Visibility */}
							{!isSponsoredAccount && !isClient && (
								<div className="mb-6 grid grid-cols-2 gap-6">
									<FormikInputWithLabel
										type="select"
										label="Profile Visibility"
										name="status"
										required
										options={ACCOUNT_STATUS_OPTIONS}
										guideLine="Control when your profile is made visible or hidden to our members."
										testId="profile-visibility"
									/>
								</div>
							)}

							{/* Company Details */}
							<div className="grid grid-cols-2 gap-x-6 gap-y-4">
								<FormikInputWithLabel
									label="Company Name"
									name="name"
									required
									testId="company-name-input"
								/>
								<FormikInputWithLabel
									label="ABN/ACN"
									name="abn"
									required
									testId="company-abn-input"
									formatValue={formatABN}
									setFieldValue={setFieldValue}
								/>
								<FormikInputWithLabel
									label="Website"
									name="website"
									testId="company-website-input"
								/>
								<SectorSelect
									label="Relevant Sectors / Experience"
									required
									name="sector_ids"
									isMulti
									selectedIds={values.sector_ids}
									onChange={handleSectorChange(setFieldValue)}
									testId="company-sectors"
								/>

								{/* Description */}
								<div className="col-span-2">
									<FormikRichTextEditor
										label="Company Description"
										name="description"
										required
										guideLine="Share a brief overview of your business, including your vision, mission, and values."
										testId="description"
									/>
								</div>

								{/* Indigenous Business */}
								<div className="col-span-2">
									<div className="flex items-center gap-4">
										<p className="font-roboto text-sm font-medium text-[#344054]">
											Are you an Indigenous Business?
										</p>
										<div
											className="flex space-x-4 font-roboto text-sm text-[#344054]"
											role="group"
											aria-labelledby="indigenous-radio-group"
										>
											<label className="cursor-pointer">
												<Field
													type="radio"
													data-testid="is-indigenous-business"
													name="indigenous_business"
													value="true"
												/>
												<span className="p-1">Yes</span>
											</label>
											<label className="cursor-pointer">
												<Field
													type="radio"
													data-testid="is-not-indigenous-business"
													name="indigenous_business"
													value="false"
												/>
												<span className="p-1">No</span>
											</label>
										</div>
									</div>
								</div>

								{/* Divider */}
								<div className="col-span-2">
									<hr className="my-4 border-gray-200" />
								</div>

								{/* Non-client fields */}
								{!isClient && (
									<>
										<CompanySizeSelect
											name="size_type_id"
											required
											profileType={profileTypeId}
											selectedId={values.size_type_id}
											onChange={handleCompanySizeChange(setFieldValue)}
											guideLine="This is the size of the business relevant to your State or Territory."
											testId="company-size-type-id"
										/>
										<FormikInputWithLabel
											label="Number of Years Trading"
											name="years_trading"
											type="number"
											required
											guideLine="Will be updated every year"
											testId="company-years-trading"
										/>
									</>
								)}

								{/* Images */}
								<FormikImagePreview
									label="Company Logo"
									name="logo"
									required
									testId="company-logo-upload"
								/>
								<FormikImagePreview
									label="Banner Image"
									name="banner_image"
									required
									guideLine="Recommended banner image resolution is 600x400 pixels for optimal display quality."
									testId="company-banner-upload"
								/>

								{/* Global Presence (non-client) */}
								{!isClient && (
									<GlobalPresenceSelect
										name="global_presence"
										required
										selectedIds={values.global_presence.map((g) => g.value)}
										onChange={handleGlobalPresenceChange(setFieldValue)}
										testId="company-global-presence"
									/>
								)}

								{/* Expertise Repeater (Consultant) */}
								{isConsultant && (
									<div className="col-span-2">
										<ExpertiseRepeater
											label="Services provided by company"
											testId="company-services"
											name="disciplines"
											values={values.disciplines}
											profileType={values.profile_type}
											required
										/>
									</div>
								)}

								{/* Speciality (HC + Sub) */}
								{(isHC || isSub) && (
									<div className="col-span-2">
										<SpecialitySelect
											label="Specialty"
											name="speciality_ids"
											required
											selectedIds={values.speciality_ids}
											onChange={handleSpecialityChange(setFieldValue)}
											testId="company-specialty"
										/>
									</div>
								)}

								{/* Trades (Sub only) */}
								{isSub && (
									<div className="col-span-2">
										<TradeSelect
											name="trade_ids"
											required
											selectedIds={values.trade_ids}
											onChange={handleTradeChange(setFieldValue)}
											testId="company-trades"
										/>
									</div>
								)}
							</div>

							{/* Contact Details */}
							<div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4">
								<FormikInputWithLabel
									label="Primary Contact Name"
									name="contact_name"
									required
									guideLine="This is the main point of contact for your account and profile."
									testId="company-primary-contact-name"
								/>
								<FormikInputWithLabel
									label="Company Phone Number"
									name="phone"
									type="tel"
									required
									testId="company-phone-number"
								/>
								<FormikInputWithLabel
									label="Company Email Address"
									name="email"
									type="email"
									required
									testId="company-email"
								/>
							</div>

							{/* Error Messages */}
							{status && !isEmpty(errors) && (
								<div className="mt-4 rounded-md bg-red-50 p-4">
									<p className="text-sm font-medium text-red-800">
										{ERROR_MESSAGES[status]?.heading || status}
									</p>
									<p className="text-sm text-red-600">
										{ERROR_MESSAGES[status]?.description || status}
									</p>
								</div>
							)}

							{businessNameError && (
								<div className="mt-4 rounded-md bg-red-50 p-4">
									<p className="text-sm font-medium text-red-800">
										{businessNameError}
									</p>
									<p className="text-sm text-red-600">
										The name has already been taken.
									</p>
								</div>
							)}

							{/* Hidden field */}
							<input type="hidden" name="company_id" value={initialValues.company_id} />

							{/* Submit */}
							<div className="mt-12 flex items-center justify-end gap-4">
								{!isEmpty(errors) && (
									<p className="text-sm text-red-500">
										There are incomplete required fields. Please complete them.
									</p>
								)}
								<button
									type="submit"
									data-testid="save-profile-button"
									disabled={isSubmitting}
									onClick={() => setShowToastOnSave(true)}
									className="flex h-[50px] items-center rounded-md bg-royal-blue px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
								>
									{isSubmitting ? "Saving..." : "Save Profile"}
								</button>
							</div>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};

export default CompanyInformationForm;
